import React, { useState, useMemo } from 'react';
import { Button } from '../ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger, DropdownMenuCheckboxItem, DropdownMenuLabel } from '../ui/dropdown-menu';
import { ChevronUp, ChevronDown, Columns3, ChevronLeft, ChevronRight, Inbox } from 'lucide-react';

/**
 * Kurumsal veri tablosu: sıralama, sayfalama, sütun görünürlüğü, sabit başlık.
 * columns: [{ key, label, align, width, sortable, value(row), render(row), hideable }]
 */
export const ProTable = ({
  columns, rows, rowKey = (r, i) => r.id || i, onRowClick, rowClassName,
  emptyText = 'Kayıt bulunamadı', emptyIcon: EmptyIcon = Inbox, footer, testid,
  initialSort, pageSizeOptions = [25, 50, 100, 250], defaultPageSize = 25, toolbarExtra,
}) => {
  const [sort, setSort] = useState(initialSort || { key: null, dir: 'desc' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [hidden, setHidden] = useState([]);

  const visible = columns.filter(c => !hidden.includes(c.key));

  const sorted = useMemo(() => {
    if (!sort.key) return rows;
    const col = columns.find(c => c.key === sort.key);
    if (!col) return rows;
    const get = col.value || ((r) => r[col.key]);
    return [...rows].sort((a, b) => {
      const va = get(a), vb = get(b);
      if (va === vb) return 0;
      if (va === null || va === undefined) return 1;
      if (vb === null || vb === undefined) return -1;
      const cmp = typeof va === 'number' && typeof vb === 'number'
        ? va - vb
        : String(va).localeCompare(String(vb), 'tr');
      return sort.dir === 'asc' ? cmp : -cmp;
    });
  }, [rows, sort, columns]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const current = Math.min(page, totalPages);
  const slice = sorted.slice((current - 1) * pageSize, current * pageSize);

  const toggleSort = (col) => {
    if (!col.sortable) return;
    setSort(s => (s.key === col.key ? { key: col.key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key: col.key, dir: 'asc' }));
    setPage(1);
  };

  return (
    <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm overflow-hidden" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-[#E2E8F0] bg-[#F8FAFC]">
        <p className="text-[12px] text-slate-600">
          <span className="font-semibold text-slate-900">{sorted.length}</span> kayıt
          {sorted.length > pageSize && <span className="text-slate-400"> · sayfa {current}/{totalPages}</span>}
        </p>
        {toolbarExtra}
        <div className="ml-auto flex items-center gap-1.5">
          <select
            value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
            className="h-8 rounded-md border border-[#E2E8F0] bg-white text-[12px] px-2 text-slate-700"
            data-testid={testid ? `${testid}-page-size` : undefined}
          >
            {pageSizeOptions.map(n => <option key={n} value={n}>{n} / sayfa</option>)}
          </select>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 text-[12px]" data-testid={testid ? `${testid}-columns` : undefined}>
                <Columns3 className="w-3.5 h-3.5 mr-1" /> Sütunlar
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel className="text-xs">Görünür sütunlar</DropdownMenuLabel>
              {columns.filter(c => c.hideable !== false).map(c => (
                <DropdownMenuCheckboxItem
                  key={c.key}
                  checked={!hidden.includes(c.key)}
                  onCheckedChange={(v) => setHidden(h => (v ? h.filter(x => x !== c.key) : [...h, c.key]))}
                  className="text-[13px]"
                >
                  {c.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="overflow-x-auto max-h-[620px] overflow-y-auto">
        <table className="w-full text-[13px]">
          <thead className="bg-white sticky top-0 z-10 shadow-[0_1px_0_0_#E2E8F0]">
            <tr>
              {visible.map(c => (
                <th
                  key={c.key}
                  onClick={() => toggleSort(c)}
                  style={c.width ? { width: c.width } : undefined}
                  className={`px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap
                    ${c.align === 'right' ? 'text-right' : c.align === 'center' ? 'text-center' : 'text-left'}
                    ${c.sortable ? 'cursor-pointer select-none hover:text-slate-800' : ''}`}
                >
                  <span className="inline-flex items-center gap-1">
                    {c.label}
                    {c.sortable && sort.key === c.key && (sort.dir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {slice.length === 0 ? (
              <tr>
                <td colSpan={visible.length} className="text-center py-14 text-slate-400">
                  <EmptyIcon className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">{emptyText}</p>
                </td>
              </tr>
            ) : slice.map((row, i) => (
              <tr
                key={rowKey(row, i)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={`border-t border-[#E2E8F0] transition-colors ${onRowClick ? 'cursor-pointer hover:bg-[#F8FAFC]' : ''} ${rowClassName ? rowClassName(row) : ''}`}
                data-testid={testid ? `${testid}-row-${rowKey(row, i)}` : undefined}
              >
                {visible.map(c => (
                  <td key={c.key} className={`px-3 py-2.5 ${c.align === 'right' ? 'text-right' : c.align === 'center' ? 'text-center' : ''} ${c.cellClass || ''}`}>
                    {c.render ? c.render(row) : (c.value ? c.value(row) : row[c.key])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {footer && slice.length > 0 && (
            <tfoot className="bg-[#F8FAFC] sticky bottom-0 border-t-2 border-[#E2E8F0]">
              {footer(visible)}
            </tfoot>
          )}
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-[#E2E8F0] bg-[#F8FAFC]">
          <p className="text-[11px] text-slate-500">
            {(current - 1) * pageSize + 1}–{Math.min(current * pageSize, sorted.length)} / {sorted.length}
          </p>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" className="h-7 px-2" disabled={current === 1}
              onClick={() => setPage(current - 1)} data-testid={testid ? `${testid}-prev` : undefined}>
              <ChevronLeft className="w-3.5 h-3.5" />
            </Button>
            <span className="text-[12px] text-slate-700 px-1 tabular-nums">{current} / {totalPages}</span>
            <Button variant="outline" size="sm" className="h-7 px-2" disabled={current === totalPages}
              onClick={() => setPage(current + 1)} data-testid={testid ? `${testid}-next` : undefined}>
              <ChevronRight className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProTable;
